import React, { useEffect, useRef, useState } from 'react';
import { getOptimizedImageUrl, normalizeImageArray } from '../../../utils/imageUtils';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getPriceShape } from '../../../utils/priceUtils';
import { addToCart } from '../../../services/apiService';
import ProductRegulationBadge from '../../common/ProductRegulationBadge';
import PriceDisplay from '../../common/PriceDisplay';
import ProductImage from '../../common/ProductImage';
import { HiHeart, HiOutlineHeart, HiOutlinePhoto, HiArrowRight, HiPlus, HiCheck } from 'react-icons/hi2';
import { activateOnKey } from '../../../utils/a11y';

/**
 * Listing card, shop-app style:
 *   - the image runs edge to edge across the top on the warm sand well, and
 *     carries the overlays — discount sticker, favourite, and the
 *     shipping / prescription badge — so the text block stays short;
 *   - below it only the title and a price row, with a round quick-add button.
 *
 * Quick add uses the same request and the same rule as the product page:
 * prescription-only (Sale.Web.Rx) or unpriced articles can't be added, so
 * they get an arrow that opens the product instead.
 *
 * Shared by /products (via ProductGrid), the category pages (CategoryResults)
 * and the generic alternatives on the product page.
 */
const ProductCard = ({ article, favorite, onFavoriteClick, onProductClick, isLoggedIn }) => {
  const { t } = useLanguage();
  const images = normalizeImageArray(article, 'F');
  const imageUrl = article.hasPhoto && images.length > 0 ? getOptimizedImageUrl(images) : null;
  const priceShape = getPriceShape(article);
  const isFavorite = !!favorite?.[article.pharmacode];
  const title = article.description?.longDescription || t('products.card.noDescription');
  const regulationCode = article.regulations?.[0]?.code?.id;
  const canQuickAdd = regulationCode !== 'Sale.Web.Rx' && priceShape.currentPrice != null;

  // Quick add: idle → adding → added (2s) → idle; a failure opens the product,
  // whose page shows the detailed error and the full add-to-cart form.
  const [cartState, setCartState] = useState('idle');
  const resetTimer = useRef(null);
  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const open = () => onProductClick(article.pharmacode);

  const handleQuickAdd = async (e) => {
    e.stopPropagation();
    if (cartState === 'adding') return;
    setCartState('adding');
    try {
      const response = await addToCart(article.pharmacode, 1);
      if (response?.success === false) throw new Error('add failed');
      setCartState('added');
      clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCartState('idle'), 2000);
    } catch {
      setCartState('idle');
      open();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={activateOnKey(open)}
      aria-label={title}
      className="group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-[1.4rem] bg-white ring-1 ring-black/[0.05] shadow-[0_14px_34px_-26px_rgba(17,24,39,0.5)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_26px_50px_-26px_rgb(var(--brand)/0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300 landing:focus-visible:ring-brand/40 sm:rounded-[1.75rem]"
    >
      {/* ---- Image, edge to edge, with its overlays ---- */}
      <div className="relative overflow-hidden bg-gradient-to-b from-[#FAF8F6] to-[#EFEBE7] [aspect-ratio:1]">
        {imageUrl ? (
          <ProductImage src={imageUrl} alt={title} pad={0.13} className="group-hover:[--img-hover:1.06]" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-300">
            <HiOutlinePhoto className="h-10 w-10" aria-hidden="true" />
          </div>
        )}

        {priceShape.inDiscount && priceShape.discountPercentage != null && (
          <span className="absolute left-2 top-2 rotate-[-6deg] rounded-full bg-sale px-2 py-0.5 text-[10px] font-extrabold text-white shadow-[0_8px_18px_-8px_rgb(var(--sale)/0.9)] transition-transform duration-300 group-hover:rotate-0 sm:left-3 sm:top-3 sm:py-1 sm:text-[11px]">
            -{priceShape.discountPercentage}%
          </span>
        )}

        {isLoggedIn && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onFavoriteClick(article.pharmacode); }}
            aria-label={t('nav.buttons.viewFavorites')}
            aria-pressed={isFavorite}
            className={`absolute right-2 top-2 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full shadow-[0_8px_18px_-10px_rgba(17,24,39,0.5)] ring-1 backdrop-blur transition-all duration-200 active:scale-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300 landing:focus-visible:ring-brand/40 sm:right-3 sm:top-3 sm:h-9 sm:w-9 ${
              isFavorite ? 'bg-brand text-white ring-transparent' : 'bg-white/85 text-brand ring-black/5 hover:bg-white'
            }`}
          >
            {isFavorite
              ? <HiHeart className="h-4 w-4 sm:h-[1.1rem] sm:w-[1.1rem]" />
              : <HiOutlineHeart className="h-4 w-4 sm:h-[1.1rem] sm:w-[1.1rem]" />}
          </button>
        )}

        {/* Shipping / prescription status, pinned to the foot of the image */}
        <ProductRegulationBadge
          regulations={article.regulations}
          className="absolute bottom-2 left-2 right-2 sm:bottom-3 sm:left-3 sm:right-3 [&>span]:shadow-[0_6px_14px_-8px_rgba(17,24,39,0.45)]"
        />
      </div>

      {/* ---- Title + price row ---- */}
      <div className="flex flex-1 flex-col px-3 pb-3 pt-2.5 sm:px-4 sm:pb-4 sm:pt-3.5">
        <h3 className="line-clamp-2 text-[12.5px] font-bold leading-snug tracking-[-0.01em] text-gray-900 transition-colors duration-200 group-hover:text-brand sm:text-sm">
          {title}
        </h3>
        <div className="hidden sm:block">
          <p className="mt-1 line-clamp-1 text-xs leading-snug text-gray-400">
            {article.description?.description || t('products.card.noDetails')}
          </p>
        </div>

        <div className="mt-auto flex items-end justify-between gap-2 pt-2.5 sm:pt-3">
          <div className="min-w-0">
            <PriceDisplay article={article} variant="card" showBadge={false} />
            <span className="hidden text-[11px] text-gray-400 sm:block">{t('products.card.includingVat')}</span>
          </div>

          {canQuickAdd ? (
            <button
              type="button"
              onClick={handleQuickAdd}
              disabled={cartState === 'adding'}
              aria-label={cartState === 'added' ? t('productDetails.added') : t('productDetails.addToCart')}
              title={t('productDetails.addToCart')}
              className={`inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-white transition-all duration-300 active:scale-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300 landing:focus-visible:ring-brand/40 focus-visible:ring-offset-2 sm:h-10 sm:w-10 ${
                cartState === 'added'
                  ? 'bg-emerald-600 shadow-[0_10px_22px_-10px_rgba(5,150,105,0.8)]'
                  : 'bg-brand shadow-[0_10px_22px_-10px_rgb(var(--brand)/0.85)] hover:bg-brand-hover'
              }`}
            >
              {cartState === 'adding' ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/70 border-t-transparent" aria-hidden="true" />
              ) : cartState === 'added' ? (
                <HiCheck className="h-5 w-5" aria-hidden="true" />
              ) : (
                <HiPlus className="h-5 w-5 transition-transform duration-300 group-hover:rotate-90" aria-hidden="true" />
              )}
            </button>
          ) : (
            <span
              aria-hidden="true"
              className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#F6F3F0] text-gray-700 transition-all duration-300 group-hover:bg-gray-900 group-hover:text-white sm:h-10 sm:w-10"
            >
              <HiArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
